import { useState } from "react";
import { ApiClientError } from "../lib/api";
import { useCastVote } from "../lib/api/ideas";
import type { VoteChoice } from "../lib/types";

interface VoteButtonsProps {
	famSlug: string;
	ideaId: string;
	// disabled true when the vote window is closed (idea resolvedAt set) — the
	// parent owns this check since it already has the Idea row in hand. Same
	// for membership: a 403 from the server clears `disabled` to false but
	// surfaces the error inline.
	disabled?: boolean;
	// Hint label rendered above the buttons when disabled (e.g. "Voting closed",
	// "Already voted", "Not a fam member").
	disabledReason?: string;
}

// VoteButtons renders the three choice buttons + handles the optimistic
// update via useCastVote. Local `lastChoice` state grays out the selected
// button after a successful cast so the user gets immediate feedback without
// fetching their own vote row separately (the server still owns the actual
// state; this is purely an interaction cue).
export function VoteButtons({
	famSlug,
	ideaId,
	disabled = false,
	disabledReason,
}: VoteButtonsProps) {
	const cast = useCastVote(famSlug, ideaId);
	const [lastChoice, setLastChoice] = useState<VoteChoice | null>(null);

	const submit = (choice: VoteChoice) => {
		setLastChoice(choice);
		cast.mutate(
			{ choice },
			{
				onError: () => {
					// Roll back the local highlight so the user can retry.
					setLastChoice(null);
				},
			},
		);
	};

	const errMessage = (() => {
		if (!cast.error) return null;
		if (cast.error instanceof ApiClientError) {
			switch (cast.error.code) {
				case "not_member":
					return "You are not a member of this Fam.";
				case "idea_expired":
					return "The voting window has expired.";
				case "idea_not_open":
					return "This idea is no longer open for voting.";
				case "not_found":
					return "Idea not found.";
				case "forbidden":
					return "You don't have permission to vote here.";
				default:
					return cast.error.message;
			}
		}
		return "Could not cast vote.";
	})();

	const isBusy = cast.isPending;

	return (
		<div className="space-y-2">
			{disabledReason && (
				<p className="text-xs text-[var(--color-muted)]">{disabledReason}</p>
			)}
			<div className="flex flex-wrap gap-2">
				<VoteButton
					label="Yes"
					tone="yes"
					onClick={() => submit("yes")}
					disabled={disabled || isBusy}
					active={lastChoice === "yes"}
				/>
				<VoteButton
					label="No"
					tone="no"
					onClick={() => submit("no")}
					disabled={disabled || isBusy}
					active={lastChoice === "no"}
				/>
			</div>
			{errMessage && (
				<p className="text-xs text-[var(--color-danger)]">{errMessage}</p>
			)}
			{lastChoice && !cast.isError && !cast.isPending && (
				<p className="text-xs text-[var(--color-muted)]">
					Vote recorded as {lastChoice}. Change it any time before the window
					closes.
				</p>
			)}
		</div>
	);
}

interface VoteButtonInnerProps {
	label: string;
	tone: VoteChoice;
	onClick: () => void;
	disabled: boolean;
	active: boolean;
}

const TONE_CLASSES: Record<VoteChoice, { idle: string; active: string }> = {
	yes: {
		idle: "border-[var(--color-border)] text-[var(--color-fg)] hover:border-emerald-500 hover:text-emerald-300",
		active: "border-emerald-500 bg-emerald-500/15 text-emerald-300",
	},
	no: {
		idle: "border-[var(--color-border)] text-[var(--color-fg)] hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]",
		active: "border-[var(--color-danger)] bg-[var(--color-danger)]/15 text-[var(--color-danger)]",
	},
};

function VoteButton({ label, tone, onClick, disabled, active }: VoteButtonInnerProps) {
	const cls = TONE_CLASSES[tone];
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={`rounded-md border px-4 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
				active ? cls.active : cls.idle
			}`}
		>
			{label}
		</button>
	);
}
